"use client";

import { useEffect, useRef } from "react";

/**
 * Auto-submits when all 6 OTP digits are filled.
 * Uses a 100ms delay to allow the UI to update before triggering submission.
 * Guards against re-submission when dependency changes (e.g. onSubmit reference)
 * cause the effect to re-run after the OTP has already been submitted.
 *
 * @param options.code - The 6-digit OTP code array
 * @param options.isReady - Whether the form is in the right step for auto-submit
 * @param options.isLoading - Whether a submission is already in progress
 * @param options.onSubmit - The submit handler (must be useCallback-wrapped by caller)
 */
export function useOtpAutoSubmit(options: {
  code: string[];
  isReady: boolean;
  isLoading: boolean;
  onSubmit: () => void;
}): void {
  const { code, isReady, isLoading, onSubmit } = options;
  const hasSubmittedRef = useRef(false);

  // Reset when code is fully cleared (user starting fresh)
  useEffect(() => {
    if (code.every((d) => !d)) {
      hasSubmittedRef.current = false;
    }
  }, [code]);

  useEffect(() => {
    if (isReady && code.every((d) => d) && !isLoading && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      const timer = setTimeout(() => {
        onSubmit();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [code, isReady, isLoading, onSubmit]);
}
